import React, { useState } from "react";
import {
    Search,
    Plus,
    Edit,
    Trash2,
    Eye,
    CreditCard,
    CalendarDays,
    EyeIcon,
    Download,
    X,
    Power,
} from "lucide-react";

const initialPlans = [
    {
        id: 1,
        name: "Basic",
        amount: 999,
        discount: 100,
        afterDiscount: 899,
        gst: 18,
        duration: 3,
        durationType: "Months",
        views: 50,
        downloads: 10,
        status: "Active",
    },
    {
        id: 2,
        name: "Premium",
        amount: 2499,
        discount: 250,
        afterDiscount: 2249,
        gst: 18,
        duration: 6,
        durationType: "Months",
        views: 150,
        downloads: 30,
        status: "Active",
    },
    {
        id: 3,
        name: "Gold",
        amount: 4999,
        discount: 500,
        afterDiscount: 4499,
        gst: 18,
        duration: 12,
        durationType: "Months",
        views: 500,
        downloads: 100,
        status: "Inactive",
    },
];

const emptyForm = {
    name: "",
    amount: "",
    discount: "",
    afterDiscount: "",
    gst: "18",
    duration: "",
    durationType: "Months",
    views: "",
    downloads: "",
    status: "Active",
};

function MembershipPlans() {

    const [plans, setPlans] = useState(initialPlans);

    const [search, setSearch] = useState("");

    const [showForm, setShowForm] = useState(false);

    const [showViewModal, setShowViewModal] = useState(false);

    const [editingPlan, setEditingPlan] = useState(null);

    const [viewingPlan, setViewingPlan] = useState(null);

    const [formData, setFormData] = useState(emptyForm);

    const isEditMode = Boolean(editingPlan);

    /* =========================
       FORM CHANGE
    ========================= */

    const handleChange = (e) => {

        const {
            name,
            value,
        } = e.target;

        setFormData((prev) => {

            const updated = {
                ...prev,
                [name]: value,
            };

            // Automatic after discount
            if (
                name === "amount" ||
                name === "discount"
            ) {

                const amount =
                    Number(
                        name === "amount"
                            ? value
                            : prev.amount
                    ) || 0;

                const discount =
                    Number(
                        name === "discount"
                            ? value
                            : prev.discount
                    ) || 0;

                updated.afterDiscount = Math.max(
                    amount - discount,
                    0
                );
            }

            return updated;
        });
    };

    /* =========================
       CREATE
    ========================= */

    const openCreateModal = () => {

        setEditingPlan(null);

        setFormData(emptyForm);

        setShowForm(true);
    };

    /* =========================
       EDIT
    ========================= */

    const openEditModal = (plan) => {

        setEditingPlan(plan);

        setFormData({
            name: plan.name,
            amount: plan.amount,
            discount: plan.discount,
            afterDiscount: plan.afterDiscount,
            gst: plan.gst,
            duration: plan.duration,
            durationType: plan.durationType,
            views: plan.views,
            downloads: plan.downloads,
            status: plan.status,
        });

        setShowForm(true);
    };

    /* =========================
       CLOSE
    ========================= */

    const closeFormModal = () => {

        setShowForm(false);

        setEditingPlan(null);

        setFormData(emptyForm);
    };

    /* =========================
       SUBMIT
    ========================= */

    const handleSubmit = (e) => {

        e.preventDefault();

        const planData = {
            name: formData.name,
            amount: Number(formData.amount),
            discount: Number(formData.discount),
            afterDiscount: Number(
                formData.afterDiscount
            ),
            gst: Number(formData.gst),
            duration: Number(formData.duration),
            durationType: formData.durationType,
            views: Number(formData.views),
            downloads: Number(formData.downloads),
            status: formData.status,
        };

        if (editingPlan) {

            setPlans((prev) =>
                prev.map((plan) =>
                    plan.id === editingPlan.id
                        ? {
                            ...plan,
                            ...planData,
                        }
                        : plan
                )
            );

        } else {

            setPlans((prev) => [
                ...prev,
                {
                    id: Date.now(),
                    ...planData,
                },
            ]);
        }

        closeFormModal();
    };

    /* =========================
       VIEW
    ========================= */

    const openViewModal = (plan) => {

        setViewingPlan(plan);

        setShowViewModal(true);
    };

    /* =========================
       STATUS
    ========================= */

    const toggleStatus = (plan) => {

        setPlans((prev) =>
            prev.map((item) =>
                item.id === plan.id
                    ? {
                        ...item,
                        status:
                            item.status === "Active"
                                ? "Inactive"
                                : "Active",
                    }
                    : item
            )
        );
    };

    /* =========================
       SEARCH
    ========================= */

    const filteredPlans = plans.filter(
        (plan) =>
            plan.name
                .toLowerCase()
                .includes(search.toLowerCase())
    );

    const activePlans = plans.filter(
        (plan) =>
            plan.status === "Active"
    ).length;

    const inactivePlans = plans.filter(
        (plan) =>
            plan.status === "Inactive"
    ).length;

    return (
        <div className="plans-page">

            {/* =========================
                PAGE HEADER
            ========================= */}

            <div className="page-header">

                <div>

                    <div className="breadcrumb">
                        Super Admin / Membership Plans
                    </div>

                    <h1>
                        Membership Plans
                    </h1>

                    <p>
                        Manage matrimonial membership plans,
                        pricing and profile access.
                    </p>

                </div>

                <button
                    className="primary-button"
                    onClick={openCreateModal}
                >
                    <Plus size={18} />
                    Add Plan
                </button>

            </div>

            {/* =========================
                SUMMARY
            ========================= */}

            <div className="plan-summary">

                <div className="summary-card">

                    <div className="summary-icon blue">
                        <CreditCard size={21} />
                    </div>

                    <div>
                        <span>Total Plans</span>

                        <strong>
                            {plans.length}
                        </strong>
                    </div>

                </div>

                <div className="summary-card">

                    <div className="summary-icon green">
                        <Power size={21} />
                    </div>

                    <div>
                        <span>Active Plans</span>

                        <strong>
                            {activePlans}
                        </strong>
                    </div>

                </div>

                <div className="summary-card">

                    <div className="summary-icon pink">
                        <Power size={21} />
                    </div>

                    <div>
                        <span>Inactive Plans</span>

                        <strong>
                            {inactivePlans}
                        </strong>
                    </div>

                </div>

            </div>

            {/* =========================
                PLAN LIST
            ========================= */}

            <div className="plans-card">

                <div className="list-header">

                    <div>

                        <h2>
                            Membership Plan List
                        </h2>

                        <span>
                            {filteredPlans.length} plans found
                        </span>

                    </div>

                    <div className="branch-search">

                        <Search size={18} />

                        <input
                            type="text"
                            placeholder="Search plan..."
                            value={search}
                            onChange={(e) =>
                                setSearch(e.target.value)
                            }
                        />

                    </div>

                </div>

                <div className="table-wrapper">

                    <table className="plan-table">

                        <thead>

                            <tr>
                                <th>Plan</th>
                                <th>Price</th>
                                <th>GST</th>
                                <th>Duration</th>
                                <th>Views</th>
                                <th>Downloads</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>

                        </thead>

                        <tbody>

                            {filteredPlans.map(
                                (plan) => (

                                    <tr key={plan.id}>

                                        <td>

                                            <div className="plan-name-cell">

                                                <div className="plan-avatar">
                                                    <CreditCard
                                                        size={18}
                                                    />
                                                </div>

                                                <div>

                                                    <strong>
                                                        {plan.name}
                                                    </strong>

                                                    <span>
                                                        ₹
                                                        {plan.amount.toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </span>

                                                </div>

                                            </div>

                                        </td>

                                        <td>

                                            <div className="price-cell">

                                                <del>
                                                    ₹
                                                    {plan.amount.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </del>

                                                <strong>
                                                    ₹
                                                    {plan.afterDiscount.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </strong>

                                            </div>

                                        </td>

                                        <td>
                                            {plan.gst}%
                                        </td>

                                        <td>

                                            {plan.duration}{" "}
                                            {plan.durationType}

                                        </td>

                                        <td>

                                            <div className="usage-cell">

                                                <EyeIcon
                                                    size={15}
                                                />

                                                {plan.views}

                                            </div>

                                        </td>

                                        <td>

                                            <div className="usage-cell">

                                                <Download
                                                    size={15}
                                                />

                                                {plan.downloads}

                                            </div>

                                        </td>

                                        <td>

                                            <button
                                                className={`status-badge ${
                                                    plan.status ===
                                                    "Active"
                                                        ? "active"
                                                        : "inactive"
                                                }`}
                                                onClick={() =>
                                                    toggleStatus(
                                                        plan
                                                    )
                                                }
                                            >
                                                {plan.status}
                                            </button>

                                        </td>

                                        <td>

                                            <div className="action-buttons">

                                                <button
                                                    className="action-button view"
                                                    onClick={() =>
                                                        openViewModal(
                                                            plan
                                                        )
                                                    }
                                                >
                                                    <Eye size={16} />
                                                </button>

                                                <button
                                                    className="action-button edit"
                                                    onClick={() =>
                                                        openEditModal(
                                                            plan
                                                        )
                                                    }
                                                >
                                                    <Edit size={16} />
                                                </button>

                                            </div>

                                        </td>

                                    </tr>

                                )
                            )}

                        </tbody>

                    </table>

                    {filteredPlans.length === 0 && (

                        <div className="empty-state">

                            <CreditCard size={42} />

                            <h3>
                                No plans found
                            </h3>

                            <p>
                                Create a membership plan
                                to get started.
                            </p>

                        </div>

                    )}

                </div>

            </div>

            {/* ==================================================
                CREATE / EDIT MODAL
            ================================================== */}

            {showForm && (

                <div className="branch-modal-overlay">

                    <div className="plan-modal">

                        <div className="modal-header">

                            <div>

                                <h2>
                                    {isEditMode
                                        ? "Edit Membership Plan"
                                        : "Add Membership Plan"}
                                </h2>

                                <p>
                                    {isEditMode
                                        ? "Update membership plan details."
                                        : "Create a new membership plan."}
                                </p>

                            </div>

                            <button
                                className="modal-close"
                                onClick={closeFormModal}
                            >
                                <X size={21} />
                            </button>

                        </div>

                        <form
                            className="plan-form"
                            onSubmit={handleSubmit}
                        >

                            <div className="form-section-title">
                                Plan Information
                            </div>

                            {/* Plan Name */}

                            <div className="form-field full">

                                <label>
                                    Plan Name
                                    <span>*</span>
                                </label>

                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Example: Premium Plan"
                                    required
                                />

                            </div>

                            {/* Amount */}

                            <div className="form-field">

                                <label>
                                    Plan Amount
                                    <span>*</span>
                                </label>

                                <input
                                    type="number"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    placeholder="Enter plan amount"
                                    min="0"
                                    required
                                />

                            </div>

                            {/* Discount */}

                            <div className="form-field">

                                <label>
                                    Plan Discount Amount
                                </label>

                                <input
                                    type="number"
                                    name="discount"
                                    value={formData.discount}
                                    onChange={handleChange}
                                    placeholder="Enter discount"
                                    min="0"
                                />

                            </div>

                            {/* After Discount */}

                            <div className="form-field">

                                <label>
                                    Plan After Discount Amount
                                    <span>*</span>
                                </label>

                                <input
                                    type="number"
                                    name="afterDiscount"
                                    value={formData.afterDiscount}
                                    readOnly
                                />

                            </div>

                            {/* GST */}

                            <div className="form-field">

                                <label>
                                    GST (%)
                                    <span>*</span>
                                </label>

                                <select
                                    name="gst"
                                    value={formData.gst}
                                    onChange={handleChange}
                                    required
                                >

                                    <option value="0">
                                        0%
                                    </option>

                                    <option value="5">
                                        5%
                                    </option>

                                    <option value="12">
                                        12%
                                    </option>

                                    <option value="18">
                                        18%
                                    </option>

                                    <option value="28">
                                        28%
                                    </option>

                                </select>

                            </div>

                            {/* Duration */}

                            <div className="form-field">

                                <label>
                                    Plan Duration
                                    <span>*</span>
                                </label>

                                <input
                                    type="number"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleChange}
                                    placeholder="Example: 6"
                                    min="1"
                                    required
                                />

                            </div>

                            {/* Duration Type */}

                            <div className="form-field">

                                <label>
                                    Duration Type
                                    <span>*</span>
                                </label>

                                <select
                                    name="durationType"
                                    value={
                                        formData.durationType
                                    }
                                    onChange={handleChange}
                                    required
                                >

                                    <option value="Days">
                                        Days
                                    </option>

                                    <option value="Months">
                                        Months
                                    </option>

                                    <option value="Years">
                                        Years
                                    </option>

                                </select>

                            </div>

                            {/* Views */}

                            <div className="form-field">

                                <label>
                                    No. of Profile Views
                                    <span>*</span>
                                </label>

                                <input
                                    type="number"
                                    name="views"
                                    value={formData.views}
                                    onChange={handleChange}
                                    placeholder="Example: 100"
                                    min="0"
                                    required
                                />

                            </div>

                            {/* Downloads */}

                            <div className="form-field">

                                <label>
                                    No. of Profile Downloads
                                    <span>*</span>
                                </label>

                                <input
                                    type="number"
                                    name="downloads"
                                    value={formData.downloads}
                                    onChange={handleChange}
                                    placeholder="Example: 25"
                                    min="0"
                                    required
                                />

                            </div>

                            {/* Status */}

                            <div className="form-field">

                                <label>
                                    Plan Status
                                    <span>*</span>
                                </label>

                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    required
                                >

                                    <option value="Active">
                                        Active
                                    </option>

                                    <option value="Inactive">
                                        Inactive
                                    </option>

                                </select>

                            </div>

                            {/* Actions */}

                            <div className="form-actions">

                                <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={closeFormModal}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="primary-button"
                                >

                                    {isEditMode ? (
                                        <>
                                            <Edit size={17} />
                                            Update Plan
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={17} />
                                            Create Plan
                                        </>
                                    )}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

            {/* ==================================================
                VIEW MODAL
            ================================================== */}

            {showViewModal && viewingPlan && (

                <div className="branch-modal-overlay">

                    <div className="plan-view-modal">

                        <div className="modal-header">

                            <div>

                                <h2>
                                    Membership Plan Details
                                </h2>

                                <p>
                                    View plan information
                                </p>

                            </div>

                            <button
                                className="modal-close"
                                onClick={() =>
                                    setShowViewModal(false)
                                }
                            >
                                <X size={21} />
                            </button>

                        </div>

                        <div className="plan-view-content">

                            <div className="plan-view-header">

                                <div className="large-plan-avatar">
                                    <CreditCard size={25} />
                                </div>

                                <div>

                                    <h3>
                                        {viewingPlan.name}
                                    </h3>

                                    <span
                                        className={`status-badge ${
                                            viewingPlan.status ===
                                            "Active"
                                                ? "active"
                                                : "inactive"
                                        }`}
                                    >
                                        {viewingPlan.status}
                                    </span>

                                </div>

                            </div>

                            <div className="plan-price-box">

                                <div>
                                    <span>
                                        Original Amount
                                    </span>

                                    <strong>
                                        ₹
                                        {viewingPlan.amount.toLocaleString(
                                            "en-IN"
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Discount
                                    </span>

                                    <strong className="discount-text">
                                        ₹
                                        {viewingPlan.discount.toLocaleString(
                                            "en-IN"
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        After Discount
                                    </span>

                                    <strong className="final-price">
                                        ₹
                                        {viewingPlan.afterDiscount.toLocaleString(
                                            "en-IN"
                                        )}
                                    </strong>
                                </div>

                            </div>

                            <div className="view-details-grid">

                                <div className="view-detail">
                                    <label>
                                        GST
                                    </label>

                                    <strong>
                                        {viewingPlan.gst}%
                                    </strong>
                                </div>

                                <div className="view-detail">
                                    <label>
                                        Duration
                                    </label>

                                    <strong>
                                        {viewingPlan.duration}{" "}
                                        {viewingPlan.durationType}
                                    </strong>
                                </div>

                                <div className="view-detail">
                                    <label>
                                        Profile Views
                                    </label>

                                    <strong>
                                        {viewingPlan.views}
                                    </strong>
                                </div>

                                <div className="view-detail">
                                    <label>
                                        Profile Downloads
                                    </label>

                                    <strong>
                                        {viewingPlan.downloads}
                                    </strong>
                                </div>

                            </div>

                        </div>

                        <div className="view-modal-footer">

                            <button
                                className="secondary-button"
                                onClick={() =>
                                    setShowViewModal(false)
                                }
                            >
                                Close
                            </button>

                            <button
                                className="primary-button"
                                onClick={() => {
                                    setShowViewModal(false);

                                    openEditModal(
                                        viewingPlan
                                    );
                                }}
                            >
                                <Edit size={17} />
                                Edit Plan
                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}

export default MembershipPlans;